import React, {useState, useEffect, useRef, useMemo, useCallback} from 'react';
import PropTypes from 'prop-types';
import {useParams} from 'react-router-dom';
import {
  Box,
  Stack,
  Typography,
  TextField,
  Button,
  Paper,
  Chip,
  IconButton,
  Tooltip,
} from '@mui/material';
import {ArrowUp, X} from 'lucide-react';
import {useFlags} from 'launchdarkly-react-client-sdk';
import {marked} from 'marked';

import {useNavigationGuard} from '../../context/NavigationGuardContext';
import {
  useAgentChat,
  ACTIVITY_ENTRY_TYPE,
} from '../../context/AgentChatContext';
import {ConversationHeader} from '../agent-chat/ConversationHeader';
import EmptyMessage from '../agent-chat/EmptyMessage';
import ThinkingBlock, {
  ThinkingIndicator,
  ToolActivityRow,
} from '../agent-chat/ThinkingBlock';
import FileUploadButton from '../agent-chat/FileUploadButton';
import MessageAttachments from '../agent-chat/MessageAttachments';
import InlineError from '../common/InlineError';
import {DrawerHeader} from '../common/DrawerHeader';
import {trackProductEvent} from '../../util';
import {TEAL_PRIMARY} from '../../theme';

import '../agent-chat/AgentChat.css';
import '../agent-chat/Markdown.css';

const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

const renderer = new marked.Renderer();
const defaultLinkRenderer = renderer.link.bind(renderer);
renderer.link = function (token) {
  const html = defaultLinkRenderer(token);
  if (token.href === '#contact-support') {
    return html;
  }
  return html.replace('<a ', '<a target="_blank" rel="noopener noreferrer" ');
};

marked.setOptions({
  breaks: true,
  gfm: true,
  headerIds: false,
  mangle: false,
  sanitize: false,
  renderer,
});

const renderMarkdown = (content) => {
  if (!content) return '';
  try {
    return marked(content);
  } catch (err) {
    console.error('Markdown parsing error:', err);
    return content;
  }
};

/**
 * Computes the ordered list of content segments and interleaved afterContent
 * activity entries for a message.
 *
 * Returns { segments, suffix } where:
 *   segments: [{slice, sliceStart, sliceEnd, entry, origIdx}]
 *     slice:      the raw content substring before this entry
 *     sliceStart: start index of the slice in rawContent
 *     sliceEnd:   end index (exclusive) of the slice in rawContent
 *   suffix:   trailing content after the last afterContent entry
 */
export const computeActivitySegments = (rawContent, activityLog) => {
  const afterEntries =
    activityLog?.reduce((acc, entry, i) => {
      if (entry.afterContent) acc.push({entry, origIdx: i});
      return acc;
    }, []) || [];

  const segments = [];
  let prevLen = 0;
  afterEntries.forEach(({entry, origIdx}) => {
    const splitLen =
      entry.contentAtEntry != null ? entry.contentAtEntry.length : prevLen;
    segments.push({
      slice: rawContent ? rawContent.slice(prevLen, splitLen) : '',
      sliceStart: prevLen,
      sliceEnd: splitLen,
      entry,
      origIdx,
    });
    prevLen = splitLen;
  });
  const suffix = rawContent ? rawContent.slice(prevLen) : '';
  return {segments, suffix};
};

const AgentChatComponent = React.memo(
  ({open, navigation, initialMessage = null}) => {
    const {documentId} = useParams();
    const {exposePennyThinking} = useFlags();
    const {runGuarded} = useNavigationGuard();

    const {
      messages,
      conversationId,
      conversations,
      error,
      isLoading,
      isLoadingConversations,
      failedMessage,
      currentStreamedMessage,
      uploadedFiles,
      isUploadingFiles,
      fileUploadError,
      setFileUploadError,
      isCreatingConversation,
      sendMessage,
      fetchConversations,
      createNewConversation,
      loadConversation,
      deleteConversation,
      renameConversation,
      clearCurrentConversation,
      handleFilesSelected,
      handleRemoveFile,
      initializeChat,
      sendInitialMessage,
      resetForUnmount,
    } = useAgentChat();

    // On mount: reset if streaming completed while unmounted
    // On unmount: reset if not currently streaming
    useEffect(() => {
      resetForUnmount();
      return () => resetForUnmount();
    }, [resetForUnmount]);

    // Local UI state
    const [inputValue, setInputValue] = useState('');
    const [isDragOver, setIsDragOver] = useState(false);
    const expandedThinkingRef = useRef(new Set());
    // Cache onExpandedChange handlers per entryKey so ThinkingBlock useEffect deps
    // get a stable reference across renders — without this, every render during
    // streaming creates a new function and re-fires both auto-expand/collapse effects.
    const expandedHandlersRef = useRef({});
    // Cache rendered markdown HTML for stable content slices (fixed-boundary segments
    // created by mid-response tool calls). Avoids re-parsing on every streaming token.
    const renderedSlicesCache = useRef({});
    const messagesEndRef = useRef(null);

    useEffect(() => {
      expandedThinkingRef.current.clear();
      expandedHandlersRef.current = {};
      renderedSlicesCache.current = {};
    }, [conversationId]);

    const makeExpandedChangeHandler = useCallback((id) => {
      expandedHandlersRef.current[id] ??= (isExpanded) => {
        if (isExpanded) {
          expandedThinkingRef.current.add(id);
        } else {
          expandedThinkingRef.current.delete(id);
        }
      };
      return expandedHandlersRef.current[id];
    }, []);

    const isInputDisabled =
      !conversationId ||
      isLoading ||
      isLoadingConversations ||
      isCreatingConversation.current;

    // Derived state — depend only on content so thinking_delta updates don't re-parse markdown
    const streamedContent = currentStreamedMessage?.content;
    const currentStreamedMarkdown = useMemo(
      () => (streamedContent ? renderMarkdown(streamedContent) : ''),
      [streamedContent]
    );

    const mergedMessages = useMemo(() => {
      if (!messages || messages.length === 0) return [];
      const result = [];
      for (let i = 0; i < messages.length; i++) {
        const msg = messages[i];
        const prevResult = result[result.length - 1];
        const msgRole = msg.role?.toUpperCase?.();
        const prevRole = prevResult?.role?.toUpperCase?.();
        if (prevResult && msgRole === 'AGENT' && prevRole === 'AGENT') {
          prevResult.content = prevResult.content + '\n\n' + msg.content;
          if (msg.attachments) {
            prevResult.attachments = [
              ...(prevResult.attachments || []),
              ...msg.attachments,
            ];
          }
          if (msg.activityLog?.length) {
            prevResult.activityLog = [
              ...(prevResult.activityLog || []),
              ...msg.activityLog,
            ];
          }
          prevResult.renderedContent = renderMarkdown(prevResult.content);
        } else {
          const merged = {...msg};
          if (msgRole === 'AGENT') {
            merged.renderedContent = renderMarkdown(msg.content);
          }
          result.push(merged);
        }
      }
      return result;
    }, [messages]);

    // Auto-scroll on new messages
    useEffect(() => {
      if (messagesEndRef.current) {
        messagesEndRef.current.scrollIntoView({behavior: 'smooth'});
      }
    }, [
      messages,
      currentStreamedMessage?.id,
      currentStreamedMessage?.content?.length,
      currentStreamedMessage?.activityLog?.length,
    ]);

    // Track open/close
    useEffect(() => {
      if (open) {
        trackProductEvent('Penny Chat Opened', {
          document_id: documentId,
          conversation_id: conversationId,
        });
      } else {
        trackProductEvent('Penny Chat Closed', {
          document_id: documentId,
          conversation_id: conversationId,
          message_count: messages.length,
        });
      }
    }, [open]);

    // Initialize chat when opened
    useEffect(() => {
      initializeChat(open, initialMessage);
    }, [open, conversationId, documentId, initialMessage]);

    // Send pending initial message when conversation is ready
    useEffect(() => {
      sendInitialMessage();
    }, [conversationId, sendInitialMessage]);

    // Handlers
    const handleSendMessage = useCallback(() => {
      const text = inputValue.trim();
      if (!text) return;
      setInputValue('');
      sendMessage(null, text);
    }, [inputValue, sendMessage]);

    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSendMessage();
      }
    };

    const handleRetry = useCallback(() => {
      if (failedMessage) {
        sendMessage(failedMessage);
      } else if (conversationId) {
        loadConversation(conversationId);
      } else {
        fetchConversations();
      }
    }, [conversationId, failedMessage]);

    const handleBack = useCallback(
      () => runGuarded(() => navigation.goBack()),
      [navigation, runGuarded]
    );

    // Drag-and-drop handlers
    const handleDragOver = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
    }, []);

    const handleDragEnter = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      if (e.dataTransfer.items?.length > 0) setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback((e) => {
      e.preventDefault();
      e.stopPropagation();
      if (!e.currentTarget.contains(e.relatedTarget)) setIsDragOver(false);
    }, []);

    const handleDrop = useCallback(
      (e) => {
        e.preventDefault();
        e.stopPropagation();
        setIsDragOver(false);

        if (isInputDisabled) return;

        const files = [...(e.dataTransfer.files || [])];
        if (files.length > 0) handleFilesSelected(files);
      },
      [isInputDisabled, handleFilesSelected]
    );

    const shouldShowEmptyMessage =
      !isLoading &&
      (!isLoadingConversations || !isCreatingConversation.current) &&
      !error &&
      messages.length === 0 &&
      !currentStreamedMessage &&
      open;

    const isStreamingThought = !currentStreamedMessage?.content;

    const handleContentLinkClick = useCallback((e) => {
      const link = e.target.closest('a[href="#contact-support"]');
      if (link) {
        e.preventDefault();
        window.Intercom('show');
      }
    }, []);

    // origIdx is the entry's index in the full activityLog for stable keys.
    const renderActivityEntry = (entry, origIdx, messageId, isStreaming) => {
      if (entry.type === ACTIVITY_ENTRY_TYPE.THINKING) {
        if (!exposePennyThinking) return null;
        const entryKey = `${messageId}-${origIdx}`;
        return (
          <ThinkingBlock
            key={entryKey}
            thinkingBlocks={entry.blocks}
            thinkingDurationMs={entry.durationMs}
            isStreaming={isStreaming && !entry.isComplete}
            defaultExpanded={expandedThinkingRef.current.has(entryKey)}
            onExpandedChange={makeExpandedChangeHandler(entryKey)}
          />
        );
      }
      if (entry.type === ACTIVITY_ENTRY_TYPE.TOOL) {
        return (
          <ToolActivityRow
            key={`tool-${origIdx}`}
            toolName={entry.toolName}
            activeLabel={entry.activeLabel}
            completeLabel={entry.completeLabel}
            isComplete={entry.isComplete}
          />
        );
      }
      return null;
    };

    // Render the markdown content area, interleaving any afterContent activity entries
    // at the position in the text where they occurred (using entry.contentAtEntry).
    // preRenderedHtml is used as-is when there are no afterContent entries (avoids
    // re-parsing when the caller already has a memoized rendered version).
    const renderContentArea = (
      rawContent,
      preRenderedHtml,
      activityLog,
      messageId,
      isStreaming
    ) => {
      const {segments, suffix} = computeActivitySegments(
        rawContent,
        activityLog
      );

      if (!segments.length) {
        return (
          <Box
            className="markdown-content"
            onClick={handleContentLinkClick}
            dangerouslySetInnerHTML={{
              __html: preRenderedHtml ?? renderMarkdown(rawContent),
            }}
          />
        );
      }

      const elements = [];
      segments.forEach(
        ({slice, sliceStart, sliceEnd, entry, origIdx}, segIdx) => {
          if (slice) {
            // Cache rendered HTML for fixed-boundary slices — boundaries don't change
            // once set, so re-parsing on every streaming token is unnecessary.
            const sliceCacheKey = `${messageId}_${sliceStart}_${sliceEnd}`;
            const cache = renderedSlicesCache.current;
            if (!cache[sliceCacheKey]) {
              cache[sliceCacheKey] = renderMarkdown(slice);
            }
            elements.push(
              <Box
                key={`content-${segIdx}`}
                className="markdown-content"
                onClick={handleContentLinkClick}
                dangerouslySetInnerHTML={{__html: cache[sliceCacheKey]}}
              />
            );
          }
          const rendered = renderActivityEntry(
            entry,
            origIdx,
            messageId,
            isStreaming
          );
          if (rendered) elements.push(rendered);
        }
      );
      // Remaining content after the last entry — suffix grows during streaming so
      // it is not cached (every token produces a unique value anyway).
      if (suffix) {
        elements.push(
          <Box
            key="content-suffix"
            className="markdown-content"
            onClick={handleContentLinkClick}
            dangerouslySetInnerHTML={{__html: renderMarkdown(suffix)}}
          />
        );
      }
      return elements;
    };

    return (
      <Stack
        direction="column"
        flex={1}
        sx={{height: '100%', overflow: 'hidden'}}
        className="agent-chat-drawer"
      >
        <DrawerHeader>
          <ConversationHeader
            conversationId={conversationId}
            conversations={conversations}
            clearCurrentConversation={clearCurrentConversation}
            createNewConversation={() => runGuarded(createNewConversation)}
            deleteConversation={deleteConversation}
            renameConversation={renameConversation}
            loadConversation={loadConversation}
            onBack={handleBack}
            isStreaming={!!currentStreamedMessage}
            shouldShowEmptyMessage={shouldShowEmptyMessage}
          />
        </DrawerHeader>

        <Box
          className="main-content"
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box
            className="message-container"
            sx={{flex: 1, overflowY: 'auto', padding: '1rem'}}
          >
            {shouldShowEmptyMessage ? (
              <EmptyMessage
                setInputValue={setInputValue}
                sendMessage={sendMessage}
              />
            ) : (
              <Stack spacing={1} sx={{width: '100%'}}>
                {mergedMessages.map((message) => {
                  const role = message.role?.toUpperCase?.();
                  return message.error_code ? (
                    <Box key={message.id} className="error-message">
                      <InlineError
                        message={message.content}
                        onRetry={handleRetry}
                        compact
                      />
                    </Box>
                  ) : (
                    <Paper
                      key={message.id}
                      className={
                        role === 'USER' ? 'user-message' : 'agent-message'
                      }
                      sx={{
                        backgroundColor:
                          role === 'USER' ? '#f2f4f4' : '#ffffff',
                      }}
                      elevation={0}
                    >
                      {role === 'USER' ? (
                        <>
                          <Typography variant="body2">
                            {message.content}
                          </Typography>
                          <MessageAttachments
                            attachments={message.attachments}
                            conversationId={conversationId}
                            messageId={message.id}
                          />
                        </>
                      ) : (
                        <>
                          {message.activityLog?.map((entry, i) =>
                            entry.afterContent
                              ? null
                              : renderActivityEntry(entry, i, message.id, false)
                          )}
                          {renderContentArea(
                            message.content,
                            message.renderedContent,
                            message.activityLog,
                            message.id,
                            false
                          )}
                          <MessageAttachments
                            attachments={message.attachments}
                            conversationId={conversationId}
                            messageId={message.id}
                          />
                        </>
                      )}
                    </Paper>
                  );
                })}

                {currentStreamedMessage && (
                  <Paper
                    key={currentStreamedMessage.id}
                    className="agent-message"
                    elevation={0}
                    data-dd-privacy="hidden"
                  >
                    {currentStreamedMessage.activityLog?.map((entry, i) =>
                      entry.afterContent
                        ? null
                        : renderActivityEntry(
                            entry,
                            i,
                            currentStreamedMessage.id,
                            true
                          )
                    )}
                    {!currentStreamedMessage.activityLog?.length &&
                      isStreamingThought && (
                        <ThinkingIndicator label="Thinking..." />
                      )}
                    {renderContentArea(
                      currentStreamedMessage.content,
                      currentStreamedMarkdown,
                      currentStreamedMessage.activityLog,
                      currentStreamedMessage.id,
                      true
                    )}
                  </Paper>
                )}

                {isLoading && !currentStreamedMessage && (
                  <Box className="loading-indicator">
                    <ThinkingIndicator label="Thinking..." />
                  </Box>
                )}

                {error && (
                  <Box className="error-message">
                    <InlineError
                      message={error}
                      onRetry={handleRetry}
                      compact
                    />
                  </Box>
                )}

                <div ref={messagesEndRef} />
              </Stack>
            )}
          </Box>

          <Box
            className="footer"
            sx={{padding: '1rem', borderTop: '1px solid #E1E5E4'}}
          >
            <Box
              onDragOver={handleDragOver}
              onDragEnter={handleDragEnter}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              sx={{
                position: 'relative',
                ...(isDragOver && {
                  '&::after': {
                    content: '"Drop files here to upload"',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(76, 175, 80, 0.1)',
                    border: '2px dashed #4caf50',
                    borderRadius: 1,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: '14px',
                    fontWeight: 500,
                    color: '#2e7d32',
                    zIndex: 1000,
                    pointerEvents: 'none',
                  },
                }),
              }}
            >
              <Tooltip
                title={
                  currentStreamedMessage
                    ? 'Please wait for the current response to complete'
                    : ''
                }
                placement="top"
                enterDelay={1000}
              >
                <div>
                  <TextField
                    fullWidth
                    placeholder="Ask any question..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    multiline
                    rows={3}
                    disabled={currentStreamedMessage !== null}
                    className="chat-input"
                    InputProps={{
                      startAdornment:
                        uploadedFiles.length > 0 || fileUploadError ? (
                          <Box
                            sx={{
                              mr: 1,
                              mb: 1,
                              display: 'flex',
                              flexDirection: 'column',
                              alignItems: 'flex-start',
                              width: '100%',
                              padding: '0 0.5rem',
                              gap: 0.5,
                            }}
                          >
                            {uploadedFiles.length > 0 && (
                              <Stack
                                direction="row"
                                spacing={0.5}
                                flexWrap="wrap"
                                useFlexGap
                                sx={{
                                  alignItems: 'flex-start',
                                  justifyContent: 'flex-start',
                                }}
                              >
                                {uploadedFiles.map((file) => (
                                  <Chip
                                    key={file.id}
                                    label={
                                      <Box
                                        sx={{
                                          display: 'flex',
                                          alignItems: 'center',
                                          gap: 0.5,
                                        }}
                                      >
                                        <Typography
                                          variant="caption"
                                          noWrap
                                          sx={{maxWidth: '100px'}}
                                        >
                                          {file.filename}
                                        </Typography>
                                        <Typography
                                          variant="caption"
                                          color="text.secondary"
                                        >
                                          ({formatFileSize(file.size)})
                                        </Typography>
                                      </Box>
                                    }
                                    size="small"
                                    onDelete={() => handleRemoveFile(file.id)}
                                    sx={{
                                      maxWidth: '200px',
                                      backgroundColor: 'rgba(0, 100, 0, 0.1)',
                                      color: '#2e7d32',
                                      '& .MuiChip-deleteIcon': {
                                        color: '#2e7d32',
                                      },
                                    }}
                                  />
                                ))}
                              </Stack>
                            )}
                            {fileUploadError && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: 0.5,
                                  width: '100%',
                                  justifyContent: 'flex-start',
                                }}
                              >
                                <Typography
                                  variant="caption"
                                  color="error"
                                  sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5,
                                    flex: 1,
                                    textAlign: 'left',
                                  }}
                                >
                                  ⚠️ {fileUploadError}
                                </Typography>
                                <IconButton
                                  size="small"
                                  onClick={() => setFileUploadError(null)}
                                  sx={{
                                    color: '#d32f2f',
                                    padding: '2px',
                                    '&:hover': {
                                      backgroundColor: 'rgba(211, 47, 47, 0.1)',
                                    },
                                  }}
                                >
                                  <X size={12} />
                                </IconButton>
                              </Box>
                            )}
                          </Box>
                        ) : null,
                      endAdornment: (
                        <Box className="chat-input-buttons">
                          <FileUploadButton
                            className="file-upload-button"
                            onFilesSelected={handleFilesSelected}
                            disabled={isInputDisabled}
                            isUploading={isUploadingFiles}
                          />
                          <Button
                            variant="contained"
                            color="primary"
                            className="send-button"
                            onClick={handleSendMessage}
                            disabled={!inputValue.trim() || isInputDisabled}
                            style={{
                              backgroundColor: TEAL_PRIMARY,
                              opacity:
                                !inputValue.trim() || isInputDisabled ? 0.5 : 1,
                            }}
                          >
                            <ArrowUp
                              size={16}
                              color="#ffffff"
                              strokeWidth={2}
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </Button>
                        </Box>
                      ),
                    }}
                  />
                </div>
              </Tooltip>
            </Box>
            <Box
              className="footer-note"
              variant="body2"
              sx={{mt: 1, textAlign: 'center'}}
            >
              <Typography variant="caption" sx={{opacity: 0.6}}>
                AI agent can make mistakes, please verify.
              </Typography>
            </Box>
          </Box>
        </Box>
      </Stack>
    );
  }
);

AgentChatComponent.displayName = 'AgentChatComponent';

AgentChatComponent.propTypes = {
  open: PropTypes.bool.isRequired,
  navigation: PropTypes.shape({
    goBack: PropTypes.func.isRequired,
    navigate: PropTypes.func.isRequired,
  }).isRequired,
  initialMessage: PropTypes.string,
};

export default AgentChatComponent;
